import Phaser from 'phaser';
import { INPUT } from '../constants';
import { SettingsState } from '../state/SettingsState';

// ============================================
// Step 3: Input System - Action Mapping Layer
// ============================================

// Action types that gameplay code uses (decoupled from raw input)
export type InputAction =
  | 'MOVE_UP'
  | 'MOVE_DOWN'
  | 'MOVE_LEFT'
  | 'MOVE_RIGHT'
  | 'INTERACT'      // Place tower / upgrade tower (A)
  | 'CANCEL'        // Cancel action, back
  | 'PAUSE'
  | 'CYCLE_NEXT'    // Cycle tower type forward (ZR trigger)
  | 'CYCLE_PREV'    // Cycle tower type backward (ZL trigger)
  | 'START'
  | 'DANCE'         // Start/stop dancing on tower
  | 'UPGRADE'       // Instant gem upgrade tower
  | 'SELL'          // Sell tower for coins (Y)
  | 'NEXT_WAVE';    // Advance to next wave (right bumper)

export type PlayerId = 'P1' | 'P2' | 'P3' | 'P4';
export const ALL_PLAYER_IDS: PlayerId[] = ['P1', 'P2', 'P3', 'P4'];
export type InputChannel = PlayerId | 'UI';

// Raw input to action mapping configuration
interface KeyMapping {
  code: string;
  action: InputAction;
}

interface PointerMapping {
  button: 'left' | 'right' | 'middle';
  action: InputAction;
}

interface PlayerInputConfig {
  keys: KeyMapping[];
  pointer?: PointerMapping[];
}

// Default control schemes
const P1_DEFAULT_CONFIG: PlayerInputConfig = {
  keys: [
    { code: 'KeyW', action: 'MOVE_UP' },
    { code: 'KeyS', action: 'MOVE_DOWN' },
    { code: 'KeyA', action: 'MOVE_LEFT' },
    { code: 'KeyD', action: 'MOVE_RIGHT' },
    { code: 'KeyE', action: 'INTERACT' },
    { code: 'Space', action: 'INTERACT' },
    { code: 'KeyQ', action: 'CANCEL' },
    { code: 'ShiftLeft', action: 'CANCEL' },
    { code: 'Escape', action: 'PAUSE' },
    { code: 'KeyR', action: 'CYCLE_NEXT' },
    { code: 'KeyF', action: 'CYCLE_PREV' },
    { code: 'Enter', action: 'START' },
    { code: 'KeyG', action: 'DANCE' },
    { code: 'KeyT', action: 'UPGRADE' },
    { code: 'KeyX', action: 'SELL' },
    { code: 'KeyN', action: 'NEXT_WAVE' },
  ],
  pointer: [
    { button: 'left', action: 'INTERACT' },
    { button: 'right', action: 'CANCEL' },
  ],
};

const P2_DEFAULT_CONFIG: PlayerInputConfig = {
  keys: [
    { code: 'ArrowUp', action: 'MOVE_UP' },
    { code: 'ArrowDown', action: 'MOVE_DOWN' },
    { code: 'ArrowLeft', action: 'MOVE_LEFT' },
    { code: 'ArrowRight', action: 'MOVE_RIGHT' },
    { code: 'Numpad0', action: 'INTERACT' },
    { code: 'NumpadEnter', action: 'INTERACT' },
    { code: 'ShiftRight', action: 'CANCEL' },
    { code: 'Backspace', action: 'PAUSE' },
    { code: 'NumpadAdd', action: 'CYCLE_NEXT' },
    { code: 'NumpadSubtract', action: 'CYCLE_PREV' },
    { code: 'NumpadDecimal', action: 'DANCE' },
    { code: 'NumpadMultiply', action: 'UPGRADE' },
  ],
};

// Gamepad button to action mapping (standard gamepad)
// A=build/upgrade, Y=sell, ZL/ZR=cycle tower, right bumper=next wave
const GAMEPAD_BUTTON_MAP: Record<number, InputAction> = {
  0: 'INTERACT',    // A / Cross - build tower, upgrade tower
  1: 'CANCEL',      // B / Circle
  2: 'CYCLE_PREV',  // X / Square
  3: 'SELL',        // Y / Triangle - delete tower for money
  4: 'CYCLE_PREV',  // L bumper
  5: 'NEXT_WAVE',   // R bumper - advance to next wave
  6: 'CYCLE_PREV',  // ZL / L trigger - cycle tower
  7: 'CYCLE_NEXT',  // ZR / R trigger - cycle tower
  9: 'START',       // Start
  8: 'PAUSE',       // Back/Select
  12: 'MOVE_UP',    // D-pad up
  13: 'MOVE_DOWN',  // D-pad down
  14: 'MOVE_LEFT',  // D-pad left
  15: 'MOVE_RIGHT', // D-pad right
};

// Action state for a single player
interface ActionState {
  current: boolean;
  previous: boolean;
}

// Pointer state
interface PointerState {
  worldX: number;
  worldY: number;
  tileX: number;
  tileY: number;
  isDown: boolean;
  justDown: boolean;
}

class InputRouterManager {
  private scene: Phaser.Scene | null = null;
  private enabled: boolean = true;
  private uiFocused: boolean = false;
  private menuMode: boolean = false; // Use higher deadzone for menu navigation

  // Player configurations (P1/P2 have keyboard, all can have gamepads)
  private p1Config: PlayerInputConfig = P1_DEFAULT_CONFIG;
  private p2Config: PlayerInputConfig = P2_DEFAULT_CONFIG;

  // Action states per player (supports up to 4 players)
  private playerActions: Map<PlayerId, Map<InputAction, ActionState>> = new Map();

  // Track which actions are currently held by keyboard (so gamepad doesn't override)
  private keyboardHeld: Map<PlayerId, Set<InputAction>> = new Map();

  // Gamepad tracking - maps player to gamepad index
  private gamepads: Map<number, Phaser.Input.Gamepad.Gamepad> = new Map();
  private playerGamepadIndex: Map<PlayerId, number> = new Map();

  // Pointer state (shared, typically P1 controls pointer)
  private pointerState: PointerState = {
    worldX: 0,
    worldY: 0,
    tileX: 0,
    tileY: 0,
    isDown: false,
    justDown: false,
  };

  // Tile size for grid conversion
  private tileSize: number = 64;

  constructor() {
    this.initActionStates();
  }

  private initActionStates(): void {
    const actions: InputAction[] = [
      'MOVE_UP', 'MOVE_DOWN', 'MOVE_LEFT', 'MOVE_RIGHT',
      'INTERACT', 'CANCEL', 'PAUSE', 'CYCLE_NEXT', 'CYCLE_PREV', 'START',
      'DANCE', 'UPGRADE', 'SELL', 'NEXT_WAVE'
    ];

    ALL_PLAYER_IDS.forEach(playerId => {
      const actionMap = new Map<InputAction, ActionState>();
      actions.forEach(action => {
        actionMap.set(action, { current: false, previous: false });
      });
      this.playerActions.set(playerId, actionMap);
      this.keyboardHeld.set(playerId, new Set());
      this.playerGamepadIndex.set(playerId, -1);
    });
  }

  private getPlayerActions(player: PlayerId): Map<InputAction, ActionState> {
    return this.playerActions.get(player) || new Map();
  }

  private getKeyboardHeld(player: PlayerId): Set<InputAction> {
    return this.keyboardHeld.get(player) || new Set();
  }

  init(scene: Phaser.Scene, tileSize: number = 64): void {
    this.scene = scene;
    this.tileSize = tileSize;
    this.setupKeyboardInput();
    this.setupPointerInput();
    this.setupGamepadInput();
  }

  private setupKeyboardInput(): void {
    if (!this.scene?.input.keyboard) return;

    this.scene.input.keyboard.on('keydown', (event: KeyboardEvent) => {
      if (!this.enabled) return;
      this.handleKeyDown(event.code);
    });

    this.scene.input.keyboard.on('keyup', (event: KeyboardEvent) => {
      if (!this.enabled) return;
      this.handleKeyUp(event.code);
    });
  }

  private setupPointerInput(): void {
    if (!this.scene?.input) return;

    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      this.updatePointerPosition(pointer);
    });

    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.enabled || this.uiFocused) return;
      this.pointerState.isDown = true;
      this.pointerState.justDown = true;
      this.handlePointerDown(pointer);
    });

    this.scene.input.on('pointerup', () => {
      this.pointerState.isDown = false;
    });
  }

  private setupGamepadInput(): void {
    if (!this.scene?.input.gamepad) return;

    this.scene.input.gamepad.on('connected', (pad: Phaser.Input.Gamepad.Gamepad) => {
      console.log(`[InputRouter] Gamepad connected: ${pad.id} (index: ${pad.index})`);
      this.gamepads.set(pad.index, pad);
      this.assignGamepads();
    });

    this.scene.input.gamepad.on('disconnected', (pad: Phaser.Input.Gamepad.Gamepad) => {
      console.log(`[InputRouter] Gamepad disconnected: ${pad.id}`);
      this.gamepads.delete(pad.index);
      this.assignGamepads();
    });

    // Check for already connected gamepads
    if (this.scene.input.gamepad.gamepads) {
      this.scene.input.gamepad.gamepads.forEach(pad => {
        if (pad) this.gamepads.set(pad.index, pad);
      });
      this.assignGamepads();
    }
  }

  private assignGamepads(): void {
    const indices = Array.from(this.gamepads.keys()).sort();
    this.playerGamepadIndex.set('P1', indices[0] ?? -1);
    this.playerGamepadIndex.set('P2', indices[1] ?? -1);
    this.playerGamepadIndex.set('P3', indices[2] ?? -1);
    this.playerGamepadIndex.set('P4', indices[3] ?? -1);
    
    // Initialize previous states to match current to avoid false "just pressed" on connect
    ALL_PLAYER_IDS.forEach((pid, i) => {
      if (indices[i] !== undefined) {
        const pad = this.gamepads.get(indices[i]);
        if (pad) {
          pad.buttons.forEach((button, btnIdx) => {
            const action = GAMEPAD_BUTTON_MAP[btnIdx];
            if (action) {
              const state = this.getPlayerActions(pid).get(action);
              if (state) {
                state.previous = button.pressed;
                state.current = button.pressed;
              }
            }
          });
        }
      }
    });
  }

  private handleKeyDown(code: string): void {
    // Check P1 mappings (keyboard)
    for (const mapping of this.p1Config.keys) {
      if (mapping.code === code) {
        const state = this.getPlayerActions('P1').get(mapping.action);
        if (state) state.current = true;
        this.getKeyboardHeld('P1').add(mapping.action);
      }
    }

    // Check P2 mappings (keyboard)
    for (const mapping of this.p2Config.keys) {
      if (mapping.code === code) {
        const state = this.getPlayerActions('P2').get(mapping.action);
        if (state) state.current = true;
        this.getKeyboardHeld('P2').add(mapping.action);
      }
    }
  }

  private handleKeyUp(code: string): void {
    // Check P1 mappings
    for (const mapping of this.p1Config.keys) {
      if (mapping.code === code) {
        const state = this.getPlayerActions('P1').get(mapping.action);
        if (state) state.current = false;
        this.getKeyboardHeld('P1').delete(mapping.action);
      }
    }

    // Check P2 mappings
    for (const mapping of this.p2Config.keys) {
      if (mapping.code === code) {
        const state = this.getPlayerActions('P2').get(mapping.action);
        if (state) state.current = false;
        this.getKeyboardHeld('P2').delete(mapping.action);
      }
    }
  }

  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    // In menu mode, do not map raw pointer to INTERACT/CANCEL — menus only respond to
    // explicit button clicks (hit-tested) or keyboard/gamepad INTERACT/START/CANCEL.
    if (this.menuMode) return;
    if (!this.p1Config.pointer) return;

    const button = pointer.leftButtonDown() ? 'left' :
                   pointer.rightButtonDown() ? 'right' :
                   pointer.middleButtonDown() ? 'middle' : null;

    if (!button) return;

    for (const mapping of this.p1Config.pointer) {
      if (mapping.button === button) {
        const state = this.getPlayerActions('P1').get(mapping.action);
        if (state) state.current = true;
      }
    }
  }

  private updatePointerPosition(pointer: Phaser.Input.Pointer): void {
    // Get world position (accounting for camera)
    const worldPoint = this.scene?.cameras.main.getWorldPoint(pointer.x, pointer.y);
    if (worldPoint) {
      this.pointerState.worldX = worldPoint.x;
      this.pointerState.worldY = worldPoint.y;
      this.pointerState.tileX = Math.floor(worldPoint.x / this.tileSize);
      this.pointerState.tileY = Math.floor(worldPoint.y / this.tileSize);
    }
  }

  private updateGamepadState(player: PlayerId, gamepadIndex: number): void {
    const pad = this.gamepads.get(gamepadIndex);
    const actions = this.getPlayerActions(player);
    const kbHeld = this.getKeyboardHeld(player);

    if (!pad) return;

    // Use higher deadzone for menu navigation to prevent accidental inputs
    const deadzone = this.menuMode ? INPUT.MENU_DEADZONE : INPUT.DEADZONE;

    // Update button states (but don't override keyboard-held actions with false)
    pad.buttons.forEach((button, index) => {
      const action = GAMEPAD_BUTTON_MAP[index];
      if (action) {
        const state = actions.get(action);
        if (state) {
          // Only set to false if keyboard isn't holding this action
          if (button.pressed) {
            state.current = true;
          } else if (!kbHeld.has(action)) {
            state.current = false;
          }
        }
      }
    });

    // Left stick to movement (with deadzone)
    const leftX = pad.axes[0]?.getValue() ?? 0;
    const leftY = pad.axes[1]?.getValue() ?? 0;

    // Only update from analog if d-pad isn't pressed (d-pad takes priority via button mapping above)
    const dpadUp = pad.buttons[12]?.pressed ?? false;
    const dpadDown = pad.buttons[13]?.pressed ?? false;
    const dpadLeft = pad.buttons[14]?.pressed ?? false;
    const dpadRight = pad.buttons[15]?.pressed ?? false;

    // Handle horizontal analog stick
    if (!dpadLeft && !dpadRight) {
      const leftState = actions.get('MOVE_LEFT');
      const rightState = actions.get('MOVE_RIGHT');
      
      if (Math.abs(leftX) > deadzone) {
        if (leftX < 0) {
          if (leftState) leftState.current = true;
          if (rightState && !kbHeld.has('MOVE_RIGHT')) rightState.current = false;
        } else {
          if (rightState) rightState.current = true;
          if (leftState && !kbHeld.has('MOVE_LEFT')) leftState.current = false;
        }
      } else {
        if (leftState && !kbHeld.has('MOVE_LEFT')) leftState.current = false;
        if (rightState && !kbHeld.has('MOVE_RIGHT')) rightState.current = false;
      }
    }

    // Handle vertical analog stick
    if (!dpadUp && !dpadDown) {
      const upState = actions.get('MOVE_UP');
      const downState = actions.get('MOVE_DOWN');
      
      if (Math.abs(leftY) > deadzone) {
        if (leftY < 0) {
          if (upState) upState.current = true;
          if (downState && !kbHeld.has('MOVE_DOWN')) downState.current = false;
        } else {
          if (downState) downState.current = true;
          if (upState && !kbHeld.has('MOVE_UP')) upState.current = false;
        }
      } else {
        if (upState && !kbHeld.has('MOVE_UP')) upState.current = false;
        if (downState && !kbHeld.has('MOVE_DOWN')) downState.current = false;
      }
    }
  }

  // ============================================
  // Public API - Query Methods
  // ============================================

  /**
   * Check if an action is currently pressed
   */
  isActionPressed(player: PlayerId, action: InputAction): boolean {
    return this.getPlayerActions(player).get(action)?.current ?? false;
  }

  /**
   * Check if an action was just pressed this frame
   */
  wasActionJustPressed(player: PlayerId, action: InputAction): boolean {
    const state = this.getPlayerActions(player).get(action);
    return state ? (state.current && !state.previous) : false;
  }

  /**
   * Check if an action was just released this frame
   */
  wasActionJustReleased(player: PlayerId, action: InputAction): boolean {
    const state = this.getPlayerActions(player).get(action);
    return state ? (!state.current && state.previous) : false;
  }

  /**
   * Check if any player just pressed an action
   */
  anyPlayerJustPressed(action: InputAction): PlayerId | null {
    for (const pid of ALL_PLAYER_IDS) {
      if (this.wasActionJustPressed(pid, action)) return pid;
    }
    return null;
  }

  /**
   * Check if a specific player just pressed an action (alias for wasActionJustPressed)
   */
  playerJustPressed(player: PlayerId, action: InputAction): boolean {
    return this.wasActionJustPressed(player, action);
  }

  /**
   * Get movement vector for a player (-1 to 1 for each axis)
   */
  getMovementVector(player: PlayerId): { x: number; y: number } {
    let x = 0;
    let y = 0;

    if (this.isActionPressed(player, 'MOVE_LEFT')) x -= 1;
    if (this.isActionPressed(player, 'MOVE_RIGHT')) x += 1;
    if (this.isActionPressed(player, 'MOVE_UP')) y -= 1;
    if (this.isActionPressed(player, 'MOVE_DOWN')) y += 1;

    // Normalize diagonal movement
    if (x !== 0 && y !== 0) {
      const mag = Math.sqrt(x * x + y * y);
      x /= mag;
      y /= mag;
    }

    return { x, y };
  }

  /**
   * Get pointer world position
   */
  getPointerWorldPosition(): { x: number; y: number } {
    return { x: this.pointerState.worldX, y: this.pointerState.worldY };
  }

  /**
   * Get pointer tile position
   */
  getPointerTilePosition(): { x: number; y: number } {
    return { x: this.pointerState.tileX, y: this.pointerState.tileY };
  }

  /**
   * Check if pointer just clicked
   */
  wasPointerJustDown(): boolean {
    return this.pointerState.justDown;
  }

  /**
   * Check if pointer is held down
   */
  isPointerDown(): boolean {
    return this.pointerState.isDown;
  }

  // ============================================
  // Frame Update
  // ============================================

  /**
   * Call at the START of the frame to poll gamepad state
   */
  update(): void {
    if (!this.enabled) return;

    // Update gamepad states for all players (these need to be polled each frame)
    ALL_PLAYER_IDS.forEach(pid => {
      const gpIdx = this.playerGamepadIndex.get(pid) ?? -1;
      if (gpIdx >= 0) {
        this.updateGamepadState(pid, gpIdx);
      }
    });
  }

  /**
   * Call at the END of the frame to prepare for next frame
   * This must be called AFTER all input checks are done
   */
  lateUpdate(): void {
    // Store current states as previous for next frame
    this.playerActions.forEach(actionMap => {
      actionMap.forEach(state => { state.previous = state.current; });
    });

    // Reset pointer just down
    this.pointerState.justDown = false;
  }

  // ============================================
  // Configuration
  // ============================================

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  setUIFocused(focused: boolean): void {
    this.uiFocused = focused;
  }

  setTileSize(size: number): void {
    this.tileSize = size;
  }

  /**
   * Enable menu mode for higher analog stick deadzone (less sensitive navigation)
   */
  setMenuMode(enabled: boolean): void {
    this.menuMode = enabled;
  }

  getConnectedGamepadCount(): number {
    return this.gamepads.size;
  }

  /**
   * Trigger vibration on the gamepad assigned to this player (if any).
   * No-op if player uses keyboard or gamepad doesn't support vibration.
   */
  vibratePlayerGamepad(playerId: PlayerId, durationMs: number, strongMagnitude: number = 0.8, weakMagnitude: number = 0.6): void {
    const gamepadIndex = this.playerGamepadIndex.get(playerId) ?? -1;
    if (gamepadIndex < 0) return;
    const pads = navigator.getGamepads?.();
    if (!pads) return;
    const pad = pads[gamepadIndex];
    const actuator = (pad as Gamepad & { vibrationActuator?: { playEffect: (type: string, opts: { duration: number; strongMagnitude: number; weakMagnitude: number }) => Promise<unknown> } })?.vibrationActuator;
    if (!actuator?.playEffect) return;
    actuator.playEffect('dual-rumble', {
      duration: durationMs,
      strongMagnitude,
      weakMagnitude,
    }).catch(() => {});
  }

  reset(): void {
    this.initActionStates();
    this.keyboardHeld.forEach(set => set.clear());
    this.pointerState = {
      worldX: 0, worldY: 0, tileX: 0, tileY: 0,
      isDown: false, justDown: false,
    };
  }

  destroy(): void {
    this.reset();
    this.scene = null;
    this.gamepads.clear();
  }
}

// Singleton instance
export const InputRouter = new InputRouterManager();
